
import { useState, useEffect } from "react";
import Header from "@/components/Header";
import SocialMediaIcons from "@/components/SocialMediaIcons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { Shield, ShieldCheck, ShieldOff, Loader2 } from "lucide-react";
import MFASetup from "@/components/auth/MFASetup";
import { getMFAFactors, unenrollMFA } from "@/utils/authUtils";
import { useToast } from "@/hooks/use-toast";

const SettingsPage = () => {
  const [notifications, setNotifications] = useState(true);
  const [autoTranscribe, setAutoTranscribe] = useState(false);
  const [showMFASetup, setShowMFASetup] = useState(false);
  const [isMFAEnabled, setIsMFAEnabled] = useState(false);
  const [isCheckingMFA, setIsCheckingMFA] = useState(true);
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const [isDisablingMFA, setIsDisablingMFA] = useState(false);
  
  const { user, checkMfaEnabled } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    async function checkMFA() {
      setIsCheckingMFA(true);
      try {
        // Check if MFA is enabled
        const { factors, totpEnabled } = await getMFAFactors();
        setIsMFAEnabled(totpEnabled);
        
        // Store the factor ID if available
        if (factors && factors.length > 0) {
          const totpFactors = factors.filter(f => f.factor_type === 'totp');
          if (totpFactors.length > 0) {
            setMfaFactorId(totpFactors[0].id);
          }
        }
      } catch (error) {
        console.error("Error checking MFA status:", error);
      } finally {
        setIsCheckingMFA(false);
      }
    }
    
    checkMFA();
  }, []);

  const handleDisableMFA = async () => {
    if (!mfaFactorId) return;
    
    setIsDisablingMFA(true);
    try {
      await unenrollMFA(mfaFactorId);
      setIsMFAEnabled(false);
      setMfaFactorId(null);
      toast({
        title: "MFA Disabled",
        description: "Two-factor authentication has been disabled for your account",
      });
    } catch (error) {
      console.error("Error disabling MFA:", error);
      toast({
        title: "Error",
        description: "Failed to disable two-factor authentication. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDisablingMFA(false);
    }
  };

  const handleMFASetupSuccess = () => {
    setShowMFASetup(false);
    setIsMFAEnabled(true);
    checkMfaEnabled(); // Update auth context state
    // Refresh MFA status
    getMFAFactors().then(({ factors }) => {
      if (factors && factors.length > 0) {
        const totpFactors = factors.filter(f => f.factor_type === 'totp');
        if (totpFactors.length > 0) {
          setMfaFactorId(totpFactors[0].id);
        }
      }
    });
  };

  return (
    <div className="min-h-screen bg-tokscript-bg text-tokscript-text flex flex-col">
      <Header />
      <main className="flex-1 pt-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <h1 className="text-3xl font-bold mb-6">Settings</h1>
          
          <Tabs defaultValue="account">
            <TabsList className="grid w-full grid-cols-4 mb-8">
              <TabsTrigger value="account">Account</TabsTrigger>
              <TabsTrigger value="security">Security</TabsTrigger>
              <TabsTrigger value="preferences">Preferences</TabsTrigger>
              <TabsTrigger value="subscription">Subscription</TabsTrigger>
            </TabsList>
            
            <TabsContent value="account">
              <Card className="bg-tokscript-card border-gray-800">
                <CardHeader>
                  <CardTitle>Account Information</CardTitle>
                  <CardDescription>Manage your account details and preferences.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input id="name" defaultValue={user?.user_metadata?.full_name || "User"} className="bg-gray-800 border-gray-700" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" defaultValue={user?.email || ""} className="bg-gray-800 border-gray-700" readOnly />
                  </div>
                  <Button className="mt-4 bg-tokscript-cta hover:bg-tokscript-cta/90 text-black">
                    Save Changes
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="security">
              {showMFASetup ? (
                <MFASetup 
                  onSuccess={handleMFASetupSuccess} 
                  onCancel={() => setShowMFASetup(false)} 
                />
              ) : (
                <Card className="bg-tokscript-card border-gray-800">
                  <CardHeader>
                    <CardTitle>Security Settings</CardTitle>
                    <CardDescription>Manage your account security options.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">Password</h3>
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <Label htmlFor="current-password">Current Password</Label>
                          <Input 
                            id="current-password" 
                            type="password" 
                            placeholder="••••••••" 
                            className="bg-gray-800 border-gray-700 mt-1" 
                          />
                        </div>
                        <div>
                          <Label htmlFor="new-password">New Password</Label>
                          <Input 
                            id="new-password" 
                            type="password" 
                            placeholder="••••••••" 
                            className="bg-gray-800 border-gray-700 mt-1" 
                          />
                        </div>
                        <div>
                          <Label htmlFor="confirm-password">Confirm New Password</Label>
                          <Input 
                            id="confirm-password" 
                            type="password" 
                            placeholder="••••••••" 
                            className="bg-gray-800 border-gray-700 mt-1" 
                          />
                        </div>
                        <div>
                          <Button className="bg-tokscript-cta hover:bg-tokscript-cta/90 text-black">
                            Update Password
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    <div className="border-t border-gray-800 pt-6">
                      <h3 className="text-lg font-semibold mb-4 flex items-center">
                        <Shield className="mr-2 h-5 w-5" />
                        Two-Factor Authentication (2FA)
                      </h3>
                      
                      {isCheckingMFA ? (
                        <div className="flex items-center justify-center p-4">
                          <Loader2 className="h-8 w-8 animate-spin text-tokscript-cta" />
                        </div>
                      ) : isMFAEnabled ? (
                        <div className="bg-green-500/10 border border-green-500/20 rounded-md p-4 mb-4">
                          <div className="flex items-center mb-2">
                            <ShieldCheck className="h-5 w-5 mr-2 text-green-400" />
                            <span className="font-medium">Two-factor authentication is enabled</span>
                          </div>
                          <p className="text-sm text-gray-400 mb-4">
                            Your account is protected with an authenticator app. You will need to enter a code from your authenticator app when signing in.
                          </p>
                          <Button 
                            variant="destructive" 
                            size="sm"
                            onClick={handleDisableMFA}
                            disabled={isDisablingMFA}
                          >
                            {isDisablingMFA ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Disabling...
                              </>
                            ) : (
                              <>
                                <ShieldOff className="mr-2 h-4 w-4" />
                                Disable 2FA
                              </>
                            )}
                          </Button>
                        </div>
                      ) : (
                        <div className="bg-gray-800/50 border border-gray-700 rounded-md p-4 mb-4">
                          <div className="flex items-center mb-2">
                            <ShieldOff className="h-5 w-5 mr-2 text-gray-400" />
                            <span className="font-medium">Two-factor authentication is not enabled</span>
                          </div>
                          <p className="text-sm text-gray-400 mb-4">
                            Add an extra layer of security to your account by requiring a verification code in addition to your password when signing in.
                          </p>
                          <Button 
                            onClick={() => setShowMFASetup(true)}
                            className="bg-tokscript-cta hover:bg-tokscript-cta/90 text-black"
                          >
                            <Shield className="mr-2 h-4 w-4" />
                            Set up 2FA
                          </Button>
                        </div>
                      )}
                      
                      <div className="text-sm text-gray-400 mt-2">
                        <p>Two-factor authentication adds an extra layer of security to your account by requiring access to your phone as well as your password.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
            
            <TabsContent value="preferences">
              <Card className="bg-tokscript-card border-gray-800">
                <CardHeader>
                  <CardTitle>Preferences</CardTitle>
                  <CardDescription>Customize your experience.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="notifications" className="text-base">Email Notifications</Label>
                      <p className="text-sm text-gray-400">Receive notifications when downloads are ready</p>
                    </div>
                    <Switch 
                      id="notifications" 
                      checked={notifications} 
                      onCheckedChange={setNotifications} 
                      className="data-[state=checked]:bg-tokscript-cta"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="auto-transcribe" className="text-base">Auto-Transcribe</Label>
                      <p className="text-sm text-gray-400">Automatically start transcription when adding videos</p>
                    </div>
                    <Switch 
                      id="auto-transcribe" 
                      checked={autoTranscribe} 
                      onCheckedChange={setAutoTranscribe}
                      className="data-[state=checked]:bg-tokscript-cta"
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="subscription">
              <Card className="bg-tokscript-card border-gray-800">
                <CardHeader>
                  <CardTitle>Subscription</CardTitle>
                  <CardDescription>Manage your subscription plan.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mb-6 p-4 rounded-lg bg-gradient-to-r from-tokscript-cta/20 to-purple-600/20 border border-tokscript-cta/30">
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="font-bold text-xl">Free Plan</h3>
                        <p className="text-sm text-gray-300">500 videos per collection limit</p>
                      </div>
                      <Badge className="bg-tokscript-cta text-black">Current Plan</Badge>
                    </div>
                  </div>
                  
                  <div className="p-4 rounded-lg bg-gray-800/50 border border-gray-700 cursor-pointer hover:bg-gray-800 transition">
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="font-bold text-xl">Pro Plan</h3>
                        <p className="text-sm text-gray-300">Unlimited downloads, priority processing</p>
                      </div>
                      <p className="font-bold">$12/mo</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
      <footer className="border-t border-gray-800 py-6 mt-8">
        <div className="container mx-auto flex flex-col md:flex-row justify-between items-center px-4">
          <div className="mb-4 md:mb-0">
            <div className="text-xl font-bold">
              <span className="text-white">tok</span>
              <span className="text-tokscript-cta">script</span>
            </div>
            <p className="text-sm text-gray-400 mt-1">© 2024 TokScript. All rights reserved.</p>
          </div>
          <SocialMediaIcons />
        </div>
      </footer>
    </div>
  );
};

export default SettingsPage;
